import React, { useState } from 'react';
import { Settings2, HelpCircle } from 'lucide-react';
import { getColumnByKey, getColumnLabel, getColumnDescription } from './columnsAPI';

const ColumnFilters = ({ 
  visibleColumns, 
  onColumnChange, 
  columnOrder,
  columnsMetadata = [], // Получаем метаданные от родителя
  isMetadataLoading = false // ← Добавлено состояние загрузки
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState(null);
  const [tooltipContent, setTooltipContent] = useState('');

  // Если метаданные еще загружаются, показываем disabled кнопку
  if (isMetadataLoading || !columnsMetadata || columnsMetadata.length === 0) {
    return (
      <div className="relative inline-block">
        <button
          disabled
          className="p-2 text-gray-400 bg-white rounded-md border border-gray-300 cursor-not-allowed"
          title={isMetadataLoading ? "Loading columns..." : "No columns available"}
        >
          <Settings2 size={20} />
          {isMetadataLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
        </button>
      </div>
    );
  }
  
  // Формируем список колонок для отображения в порядке columnOrder
  const displayColumns = columnOrder
    .filter(colId => colId !== 'date') // Date нельзя скрывать
    .map(colId => {
      // Ищем колонку в метаданных
      const column = getColumnByKey(colId, columnsMetadata);
      
      if (column) {
        return {
          id: column.key,
          label: column.label,
          description: column.description,
          defaultVisible: column.is_visible,
          isSystem: column.is_system || false
        };
      }
      
      return null;
    }).filter(Boolean); // Убираем null значения

  // Компонент тултипа
  const Tooltip = () => {
    if (!tooltipPosition || !tooltipContent) return null;

    return (
      <div 
        style={{
          position: 'fixed',
          top: tooltipPosition.y - 10,
          left: tooltipPosition.x,
          transform: 'translate(-50%, -100%)',
          zIndex: 9999
        }}
        className="bg-gray-900 text-white rounded-lg shadow-lg p-3 text-sm max-w-xs"
      >
        {tooltipContent}
        <div 
          className="absolute left-1/2 -bottom-1 w-2 h-2 bg-gray-900 transform -translate-x-1/2 rotate-45"
        />
      </div>
    );
  };

  const handleChange = (e, columnId) => {
    const isChecked = e.target.checked;
    
    // Проверяем, действительно ли что-то изменилось
    if ((isChecked && visibleColumns.includes(columnId)) || 
        (!isChecked && !visibleColumns.includes(columnId))) {
      return; // Ничего не делаем
    }
    
    // Создаем новый массив видимых колонок
    const newColumns = isChecked
      ? [...visibleColumns, columnId]
      : visibleColumns.filter(id => id !== columnId);
    
    // Вызываем родительский обработчик
    onColumnChange(newColumns);
  };

  const handleSelectAll = () => {
    // Date всегда видимая, добавляем остальные колонки  
    const allColumns = ['date', ...columnOrder.filter(colId => colId !== 'date')];
    onColumnChange(allColumns);
  };

  const handleDeselectAll = () => {
    // Date нельзя скрыть, оставляем только её
    onColumnChange(['date']);
  };

  const handleResetToDefault = () => {
    // Очищаем localStorage для колонок статистики
    try {
      localStorage.removeItem('statisticsVisibleColumns');
      localStorage.removeItem('statisticsColumnOrder');
      console.log('Statistics columns localStorage cleared');
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
    
    // Находим колонки по умолчанию из метаданных (включая date)
    const defaultCols = ['date', ...columnsMetadata
      .filter(col => col.is_visible === true)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
      .map(col => col.key)
      .filter(key => key !== 'date')]; // Исключаем дубли date
    
    // Применяем колонки по умолчанию
    onColumnChange(defaultCols);
    
    // Закрываем меню
    setIsOpen(false);
    
    // Перезагружаем страницу для полного сброса состояния
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  const handleShowTooltip = (e, description) => {
    if (!description) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.x + rect.width / 2,
      y: rect.y
    });
    setTooltipContent(description);
  };

  const handleHideTooltip = () => {
    setTooltipPosition(null);
    setTooltipContent('');
  };

  // Закрываем тултип при скролле
  React.useEffect(() => {
    const handleScroll = () => handleHideTooltip();
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, []);

  // Статистика видимых колонок (исключая date из подсчета)
  const visibleCount = visibleColumns.filter(col => col !== 'date').length;
  const totalCount = displayColumns.length;

  return (
    <div className="relative inline-block">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 text-gray-600 hover:text-gray-900 bg-white rounded-md border border-gray-300 hover:bg-gray-50 transition-colors"
        title="Show/Hide Columns"
      >
        <Settings2 size={20} />
        {visibleCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {visibleCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg z-50 border border-gray-200">
          <div className="p-4">
            <div className="flex justify-between items-center mb-3">
              <div className="font-medium text-gray-700">
                Show Columns
                <span className="text-sm text-gray-500 ml-2">
                  ({visibleCount}/{totalCount})
                </span>
              </div>
              <button 
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-lg leading-none"
                title="Close"
              >
                ×
              </button>
            </div>
            
            <div className="flex justify-between mb-3 text-xs gap-2">
              <button
                onClick={handleSelectAll}
                className="text-blue-600 hover:text-blue-800 transition-colors"
                title="Show all columns"
              >
                Select All
              </button>
              <button
                onClick={handleDeselectAll}
                className="text-gray-600 hover:text-gray-800 transition-colors"
                title="Hide all columns"
              >
                Deselect All
              </button>
              <button
                onClick={handleResetToDefault}
                className="text-blue-600 hover:text-blue-800 font-medium transition-colors"
                title="Reset to default columns and clear saved preferences"
              >
                Reset to Default
              </button>
            </div>
            
            <div className="space-y-1 max-h-96 overflow-y-auto pr-1">
              {displayColumns.map((column) => (
                <label 
                  key={column.id} 
                  className="flex items-center space-x-2 py-2 px-2 hover:bg-gray-50 rounded cursor-pointer group transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={visibleColumns.includes(column.id)}
                    onChange={(e) => handleChange(e, column.id)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                  />
                  <span className="text-sm text-gray-700 flex-grow">
                    {column.label}
                    {column.isSystem && (
                      <span className="text-xs text-gray-400 ml-1">(system)</span>
                    )}
                  </span>
                  
                  {/* Иконка помощи для колонок с описанием */}
                  {column.description && (
                    <div 
                      className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity cursor-help"
                      onMouseEnter={(e) => handleShowTooltip(e, column.description)}
                      onMouseLeave={handleHideTooltip}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                    </div>
                  )}
                </label>
              ))}
              
              {displayColumns.length === 0 && (
                <div className="text-center py-4 text-gray-500">
                  <div className="text-sm">No columns available</div>
                  <div className="text-xs mt-1">
                    Please check your columns configuration
                  </div>
                </div>
              )}
            </div>
            
            {/* Информация о колонках по умолчанию */}
            {columnsMetadata.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="text-xs text-gray-500">
                  <div className="flex justify-between">
                    <span>Default visible:</span>
                    <span className="font-medium">
                      {columnsMetadata.filter(col => col.is_visible).length + 1} {/* +1 для date */}
                    </span>
                  </div>
                  <div className="flex justify-between mt-1">
                    <span>Total available:</span>
                    <span className="font-medium">
                      {columnsMetadata.length + 1} {/* +1 для date */}
                    </span>
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    Date column is always visible
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Рендерим тултип */}
      <Tooltip />
    </div>
  );
};

export default ColumnFilters;